import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { SETTINGS_TEXT_KEYS } from '@/core/translations/settingsTextKeys';

import { rapidstorTextSource } from './rapidstor';

// Companion to the `rs-setting-data-attr` Vite plugin. That plugin tags any JSX
// element whose text *might* come from a RapidStor setting with a provisional
// `data-rs-setting-dynamic` attribute — provisional because the build has no
// type information. The value is either:
//   - a member-access path (`location.rapidstor.currencySymbol`), or
//   - a settings-text helper name (`payNowButtonText`).
//
// At runtime we have the real values, so here we keep only the tokens that are
// genuinely settings-controlled *text*: a path must resolve to a string (a
// boolean like `location.rapidstor.useDealCoupons`, used only in a conditional,
// is dropped); a helper must currently resolve to the setting rather than a corp
// translation. Surviving tokens are written to the final `data-rs-setting`
// attribute that Automate's translate inspector reads; if none survive the
// element is left unmarked.
const DYNAMIC_ATTR = 'data-rs-setting-dynamic';
const SETTING_ATTR = 'data-rs-setting';

// The attribute the `i18n-data-attr` build plugin writes for translated copy, and
// which Automate's inspector and preview overlay both key off. Settings-backed
// copy is overridable through Automate like any other phrase, but it is rendered
// by a helper rather than a `t()` call, so that plugin never sees it and the
// preview has no way to find the element. We publish the catalog key here, where
// the helper names are already known.
const I18N_ATTR = 'data-i18n-key';

// Walk a dotted path off a base object without assuming its shape.
const getPath = (base: unknown, segments: string[]): unknown => segments.reduce<unknown>((acc, seg) => {
        if (acc && typeof acc === 'object' && seg in (acc as object)) {
            return (acc as Record<string, unknown>)[seg];
        }
        return undefined;
    }, base);

// Resolve a captured settings path against the live globals and report whether
// it currently holds a string. Roots map to the objects the plugin captured them
// from (a leading `window.` was already stripped at build time).
const pathResolvesToString = (path: string, location: Location): boolean => {
    const [root, ...rest] = path.split('.');
    const settings = window.rapidstor_settings;
    let base: unknown;
    if (root === 'location') base = location;
    else if (root === 'rapidstor_settings') base = settings;
    else if (root === 'app_config' || root === 'appConfig') {
        base = settings?.app_config;
    } else if (root === 'descriptor') {
        // Per-descriptor content (name/description/specialText/descriptions) is
        // not on a global object, so it can't be walked here — but only string
        // copy fields are ever tagged with these paths, so treat them as live.
        return true;
    } else return false;
    return typeof getPath(base, rest) === 'string';
};

// A bare identifier (no dot) is a helper name; anything with a dot is a path.
const tokenIsLiveSetting = (token: string, location: Location): boolean => (token.includes('.')
        ? pathResolvesToString(token, location)
        : rapidstorTextSource(token, location) === 'setting');

const splitTokens = (value: string | null): string[] => (value ?? '').split(/\s+/).filter(Boolean);

/**
 * Publish the catalog key for any settings-text helper on this element, so
 * Automate can address the phrase the same way it addresses `t()` copy.
 *
 * Unioned with whatever the build plugin already wrote, and only written when the
 * set actually grows — the value is stable across passes, and the editor's
 * overlay watches this attribute.
 *
 * Done for every helper that has a key, not only the ones currently resolving to
 * a setting: the phrase stays overridable even while a bundled corp translation
 * is what happens to be showing.
 */
const publishCatalogKeys = (el: Element, tokens: string[]): void => {
    const keys = tokens
        .map((token) => (SETTINGS_TEXT_KEYS as Record<string, string | undefined>)[token])
        .filter((key): key is string => Boolean(key));
    if (!keys.length) return;

    const existing = splitTokens(el.getAttribute(I18N_ATTR));
    const merged = [...new Set([...existing, ...keys])];
    if (merged.length !== existing.length) {
        el.setAttribute(I18N_ATTR, merged.join(' '));
    }
};

const resolveElement = (el: Element, location: Location): void => {
    const tokens = splitTokens(el.getAttribute(DYNAMIC_ATTR));

    const live = tokens.filter((token) => tokenIsLiveSetting(token, location));
    if (live.length) {
        el.setAttribute(SETTING_ATTR, live.join(' '));
    } else {
        el.removeAttribute(SETTING_ATTR);
    }

    publishCatalogKeys(el, tokens);
};

// The active location is fetched asynchronously by the router loader, which sets
// it here only once a route resolves — well after the app first mounts. So it is
// read fresh on every pass, never captured up front.
const currentLocation = (): Location | undefined => window.rapidstor_settings?.rapidstor_options?.location;

const resolveAll = (root: ParentNode): void => {
    const location = currentLocation();
    if (!location) return;
    root.querySelectorAll(`[${DYNAMIC_ATTR}]`).forEach((el) => {
        resolveElement(el, location);
    });
};

/**
 * Resolve every `data-rs-setting-dynamic` element under `root` now and keep them
 * resolved as the SPA re-renders. Cheap (an attribute query + a property walk or
 * bundled corp lookup per match), debounced to one pass per frame.
 *
 * The observer is always installed, even before a location exists: the loader
 * sets the location and then renders the journey, and that render is itself a
 * mutation under `root` — so the pass it triggers runs with the location in
 * place. A pass with no location yet simply no-ops.
 */
export const installSettingsTextMarker = (root: HTMLElement): void => {
    resolveAll(root);

    let frame: number | undefined;
    const observer = new MutationObserver(() => {
        if (frame !== undefined) return;
        frame = window.requestAnimationFrame(() => {
            frame = undefined;
            resolveAll(root);
        });
    });
    // Watch for newly-mounted markers (childList) and for a dynamic value that
    // React toggles on an existing element (attributeFilter) — e.g. a button
    // whose copy switches between a setting and a translation. The filter is
    // DYNAMIC_ATTR only, so our own SETTING_ATTR writes don't retrigger the loop.
    observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [DYNAMIC_ATTR],
    });
};
